import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  CalendarDate,
  type DateValue,
  ZonedDateTime,
} from '@internationalized/date';
import { cn } from '@antimatterhq/ui/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@antimatterhq/ui/components/popover';
import { Button } from '@antimatterhq/ui/components/button';
import {
  nowInTimezone,
  getStartOfDayInTimezone,
  getEndOfDayInTimezone,
} from '@antimatterhq/ui/utils/zonedDateTimeUtils';
import { toTimeZone } from '@internationalized/date';
import { MultiDateRangePicker } from '../multiDateRangePicker/multiDateRangePicker';
import { TimeInput } from '../timeInput/timeInput';
import { DateTimeRangePickerInput } from './dateTimeRangePickerInput/dateTimeRangePickerInput';
import { timePresets } from './constants';
import { validateDateRange, validateMinDate } from './validation';
import { DateRange } from './types';

export interface DateTimeRangePickerProps {
  className?: string;
  placeholder?: string;
  dateRange: DateRange;
  onChange?: (value: DateRange) => void;
  minDate?: ZonedDateTime;
  errorMessage?: string;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  onValidationError?: (error: { type: string; message: string }) => void;
  onParseError?: (input: string, error: Error) => void;
}

export const DateTimeRangePicker = ({
  className,
  placeholder = 'Select date and time range',
  dateRange,
  onChange,
  minDate,
  timezone,
  onTimezoneChange,
  // onValidationError, // TODO: Implement validation error handling
  onParseError,
}: DateTimeRangePickerProps) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string>('');

  if (!dateRange) {
    throw new Error('DateTimeRangePicker: value prop is required');
  }

  useEffect(() => {
    if (dateRange) {
      validateDateRange(dateRange);
    }
    validateMinDate(minDate);
  }, [dateRange, minDate]);

  // Convert ZonedDateTime range to CalendarDate range for the calendar
  const calendarValue = useMemo(() => {
    if (!dateRange) return null;

    // Convert UTC dates to display timezone for calendar
    const startInTz = toTimeZone(dateRange.start, timezone);
    const endInTz = toTimeZone(dateRange.end, timezone);

    return {
      start: new CalendarDate(startInTz.year, startInTz.month, startInTz.day),
      end: new CalendarDate(endInTz.year, endInTz.month, endInTz.day),
    };
  }, [dateRange, timezone]);

  // Convert minDate to CalendarDate
  const minCalendarValue = useMemo(() => {
    if (!minDate) return undefined;

    // Convert UTC minDate to display timezone for calendar
    const minInTz = toTimeZone(minDate, timezone);
    return new CalendarDate(minInTz.year, minInTz.month, minInTz.day);
  }, [minDate, timezone]);

  // Handle calendar date selection
  const handleCalendarChange = useCallback(
    (range: { start: DateValue; end: DateValue } | null) => {
      if (!range?.start || !range?.end) return;

      const startCalendarDate =
        range.start instanceof CalendarDate
          ? range.start
          : new CalendarDate(
              range.start.year,
              range.start.month,
              range.start.day,
            );
      const endCalendarDate =
        range.end instanceof CalendarDate
          ? range.end
          : new CalendarDate(range.end.year, range.end.month, range.end.day);

      // Create ZonedDateTime directly from calendar dates in the selected timezone
      // This ensures the date boundaries are correct for the user's timezone
      const startInTz = getStartOfDayInTimezone(startCalendarDate, timezone);
      const endInTz = getEndOfDayInTimezone(endCalendarDate, timezone);

      // Convert to UTC for storage/propagation.
      const newStartUtc = toTimeZone(startInTz, 'UTC');
      const newEndUtc = toTimeZone(endInTz, 'UTC');

      setError('');
      onChange?.({ start: newStartUtc, end: newEndUtc });
    },
    [onChange, timezone],
  );

  // Handle quick preset selection
  const handleQuickSelection = useCallback(
    (preset: { getValue: (timezone: string) => DateRange }) => {
      const range = preset.getValue(timezone);

      const rangeUtc: DateRange = {
        start: toTimeZone(range.start, 'UTC'),
        end: toTimeZone(range.end, 'UTC'),
      };

      setError('');
      onChange?.(rangeUtc);
    },
    [timezone, onChange],
  );

  // Handle "Now" button click
  const handleNowClick = useCallback(() => {
    const now = nowInTimezone(timezone);
    // Set to end of current minute in selected timezone
    const nowEndOfMinute = now.set({
      second: 59,
      millisecond: 999,
    });

    const nowEndUtc = toTimeZone(nowEndOfMinute, 'UTC');

    const newRange = { ...dateRange, end: nowEndUtc };
    setError('');
    onChange?.(newRange);
  }, [dateRange, timezone, onChange]);

  // Handle timezone change
  const handleTimezoneChange = useCallback(
    (newTimezone: string) => {
      // Only update the selected timezone – internal UTC timestamps remain the same.
      onTimezoneChange(newTimezone);
    },
    [onTimezoneChange],
  );

  // Handle input click to open popover
  const handleInputClick = useCallback(() => {
    if (!open) {
      setOpen(true);
    }
  }, [open]);

  // Handle done button click
  const handleDone = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn('w-full', className)}>
          <DateTimeRangePickerInput
            placeholder={placeholder}
            onClick={handleInputClick}
            timezone={timezone}
            onTimezoneChange={handleTimezoneChange}
            value={dateRange}
            onChange={onChange}
            open={open}
            error={error}
            onParseError={onParseError}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="border-r border-gray-200 p-2 dark:border-gray-700">
            <div className="flex flex-col gap-2">
              {timePresets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  onClick={() => handleQuickSelection(preset)}
                  className="justify-start text-left"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          <MultiDateRangePicker
            value={calendarValue}
            onChange={handleCalendarChange}
            minValue={minCalendarValue}
          />
        </div>

        <div className="w-full border-t p-4">
          <div className="mb-4 flex w-full items-center justify-between gap-2">
            <TimeInput
              value={dateRange.start}
              timezone={timezone}
              onChange={(newDate) =>
                onChange?.({
                  ...dateRange,
                  start: toTimeZone(newDate, 'UTC'),
                })
              }
              secondBehaviour="start"
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">to</span>
            <div className="flex items-center gap-2">
              <Button
                size="small"
                onClick={handleNowClick}
                className="h-8 border border-gray-200 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-transparent dark:text-gray-100 dark:hover:bg-gray-800"
              >
                Now
              </Button>
              <TimeInput
                value={dateRange.end}
                timezone={timezone}
                onChange={(newDate) =>
                  onChange?.({
                    ...dateRange,
                    end: toTimeZone(newDate, 'UTC'),
                  })
                }
                secondBehaviour="end"
              />
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Button onClick={handleDone} variant="primary">
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
